import { createApi } from "@reduxjs/toolkit/query/react";

// Utils
import { gisApiBaseQuery } from "../query";

// Types
import {
  ODLGenerationV2Response,
  ODLTaskStatusResponse,
  ProjectStatus,
  ProjectStatusType,
  ProjectUpdateData,
  UploadDMAResponse,
  ViewODLsResponse
} from "@/types/portalProject.type";
import { Tag } from "../tag";
import { portalProjectsApi } from "./portalProjects.api";

export const portalProjectsGISApi = createApi({
  reducerPath: "portalProjectsGISQuery",
  baseQuery: gisApiBaseQuery,
  tagTypes: [Tag.GIS_UPLOAD_DATA, Tag.PROJECT, Tag.PROJECT_LIST],
  endpoints: (builder) => ({
    getGISUploadData: builder.query<
      {
        upload_id: number;
        company_id: number;
        odl_level?: number;
        leak_radius?: number;
      },
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `/gis_admin/project_id/${projectId}/upload-id`,
        method: "GET"
      }),
      providesTags: (res) =>
        res
          ? [
              { type: Tag.GIS_UPLOAD_DATA },
              { type: Tag.GIS_UPLOAD_DATA, id: res.upload_id }
            ]
          : [{ type: Tag.GIS_UPLOAD_DATA }]
    }),
    selectGISUpload: builder.mutation<
      number | undefined,
      { uploadId: number; projectId: number; companyId: number }
    >({
      query: ({ uploadId, projectId, companyId }) => ({
        url: `/gis_admin/existing_upload/upload_id/${uploadId}/project_id/${projectId}/company_id/${companyId}`,
        method: "GET"
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: Tag.GIS_UPLOAD_DATA, id: arg.uploadId }
      ]
    }),
    updateProject: builder.mutation<
      { upload_id: number; odl_task_id: string },
      { uploadId: number; projectId: number; body: ProjectUpdateData }
    >({
      query: ({ uploadId, projectId, body }) => ({
        url: `/gis_admin/project_id/${projectId}/upload_id/${uploadId}`,
        method: "PATCH",
        body
      })
      // onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
      //   await queryFulfilled;
      //   dispatch(
      //     portalProjectsApi.util.invalidateTags([
      //       { type: Tag.PROJECT, id: args.projectId }
      //     ])
      //   );

      //   debug.log("[API] invalidated!", { args });
      // }
      // invalidatesTags: (_result, _error, arg) => [
      //   { type: Tag.PROJECT, id: arg.projectId }
      // ]
    }),
    updateProjectStatus: builder.mutation<
      boolean,
      {
        projectId: number;
        companyId: number;
        status: ProjectStatusType;
        rejectionNotes?: string;
      }
    >({
      query: ({ projectId, companyId, status, rejectionNotes }) => ({
        // url: `/update_status/upload_id/${uploadId}/status/${status}`,
        url: `/gis_admin/update_status/project_id/${projectId}/status/${status}/company_id/${companyId}`,
        method: "PATCH",
        body: {
          notes: rejectionNotes
        }
      }),

      // TODO: Refactor redux - use injectEndpoints as opposed to having a bunch of createApi instances (for invalidation purposes between the same baseQuery)
      // *NB: workaround as GIS server and fido_api monolith are different baseQueries
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        await queryFulfilled;

        const tagsToInvalidate = [
          { type: Tag.PROJECT_LIST },
          { type: Tag.PROJECT, id: args.projectId }
        ];
        if (args.status === ProjectStatus.DELETED) {
          // We don't want to refetch single project if it's deleted
          tagsToInvalidate.pop();
        }
        dispatch(portalProjectsApi.util.invalidateTags(tagsToInvalidate));
      }
      // invalidatesTags: (_result, _error, arg) => [
      //   { type: Tag.PROJECT_LIST },
      //   { type: Tag.PROJECT, id: arg.projectId }
      // ]
    }),
    updateProjectGISData: builder.mutation<
      undefined,
      { uploadId: number; projectId: number }
    >({
      query: ({ uploadId, projectId }) => ({
        url: `/gis_admin/${uploadId}`,
        method: "PATCH",
        body: {
          project_id: projectId
        }
      })
    }),
    getUploadDMAs: builder.query<UploadDMAResponse, { uploadId: number }>({
      query: ({ uploadId }) => ({
        url: `/dma/upload/${uploadId}`,
        method: "POST"
      })
    }),

    // ODL Generation V2 endpoints
    startODLGenerationV2: builder.mutation<
      ODLGenerationV2Response,
      { companyId: number; uploadId: number }
    >({
      query: ({ companyId, uploadId }) => ({
        url: `/gis_upload/odl_generation_v2`,
        method: "GET",
        params: {
          company_id: companyId,
          upload_id: uploadId
        }
      })
    }),
    getODLTaskStatus: builder.query<ODLTaskStatusResponse, { taskId: string }>({
      query: ({ taskId }) => ({
        url: `/project_creation/odl_task/${taskId}`,
        method: "GET"
      })
    }),
    viewODLs: builder.query<
      ViewODLsResponse,
      { uploadId: number; leakRadius: number; dmaKeys: string[] }
    >({
      query: ({ uploadId, leakRadius, dmaKeys }) => ({
        url: `/project_creation/view_odls/upload_id/${uploadId}?leak_radius=${leakRadius}`,
        method: "POST",
        body: dmaKeys
      })
    })
  })
});

export const {
  useUpdateProjectStatusMutation,
  useGetGISUploadDataQuery,
  useSelectGISUploadMutation,
  useUpdateProjectGISDataMutation,

  useUpdateProjectMutation,
  useGetUploadDMAsQuery,

  // ODL Generation V2
  useStartODLGenerationV2Mutation,
  useGetODLTaskStatusQuery,
  useViewODLsQuery
} = portalProjectsGISApi;
